import { useEffect, useState } from 'react';
import { EntityBrief } from '../../types/accounts';
import { apiClient } from '../../lib/api';
import { Badge } from '../ui';

interface Props {
  entityId: string;
}

export function ClientBriefPanel({ entityId }: Props) {
  const [brief, setBrief] = useState<EntityBrief | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    setLoading(true);
    setFetchError(false);
    setBrief(null);
    apiClient
      .getEntityBrief(entityId)
      .then(setBrief)
      .catch((e: unknown) => {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 404) {
          setBrief(null); // normal "no brief yet" → empty state renders
        } else {
          setFetchError(true);
        }
      })
      .finally(() => setLoading(false));
  }, [entityId]);

  return (
    <div className="mb-4 border border-neutral-200 rounded-md overflow-hidden">
      {/* Header / toggle */}
      <button
        type="button"
        aria-expanded={open}
        aria-controls="brief-panel-content"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2 bg-neutral-50 hover:bg-neutral-100 transition"
      >
        <div className="flex items-center gap-2">
          <span className="text-13 font-semibold text-neutral-700">AI Brief</span>
          {brief?.needs_attention && (
            <Badge variant="warning" size="sm">
              Needs attention
            </Badge>
          )}
        </div>
        <span className="text-neutral-400 text-xs">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div id="brief-panel-content" className="px-3 py-3 bg-white">
          {loading ? (
            <p className="text-xs text-neutral-400 italic">Loading brief…</p>
          ) : fetchError ? (
            <p className="text-xs text-neutral-400 italic">Brief unavailable.</p>
          ) : !brief ? (
            <p className="text-xs text-neutral-400 italic">No brief available yet.</p>
          ) : (
            <>
              {brief.attention_reason && (
                <p className="text-xs text-warning-600 mb-2 font-medium">
                  {brief.attention_reason}
                </p>
              )}
              <pre className="whitespace-pre-wrap text-sm text-neutral-700 font-sans leading-relaxed">
                {brief.content_md}
              </pre>
            </>
          )}
        </div>
      )}
    </div>
  );
}
